/**
 * INFINITY LIFE 2.0 — "Meu Dia" Dashboard Component
 */

import { store } from '../state.js';
import { api, showToast } from '../api.js';
import { getPriorityMeta, getLocationMeta, formatTimeBR } from '../utils/formatters.js';

export function renderDashboard() {
  const { dashboardData, tasks, activeMode } = store.get();

  if (!dashboardData) {
    return `
      <div style="text-align: center; padding: 80px 20px;">
        <div style="font-size: 2.5rem; margin-bottom: 16px;">☀️</div>
        <h2>Carregando Meu Dia...</h2>
        <p style="color: var(--text-muted); margin-top: 8px;">Organizando compromissos e prioridades.</p>
      </div>
    `;
  }

  const { greeting, formatted_date, summary, next_upcoming, urgent_tasks, timeline } = dashboardData;
  const todayTasks = dashboardData.tasks || [];

  return `
    <div class="dashboard-view">
      <!-- Header Greeting & Date -->
      <div class="dashboard-header">
        <div>
          <h1 class="greeting-text">${greeting}</h1>
          <p class="date-text">${formatted_date}</p>
        </div>
        <div>
          <button class="btn-primary" id="btn-quick-new-task">
            <span>＋</span> <span>Nova Tarefa</span>
          </button>
        </div>
      </div>

      <!-- Quick Capture Bar -->
      <div class="quick-capture-card">
        <span style="font-size: 1.3rem;">⚡</span>
        <input type="text" id="quick-capture-input" class="quick-capture-input" 
               placeholder="Captura Rápida: Digite uma tarefa ou compromisso e pressione Enter..." />
        <button class="btn-secondary" id="btn-quick-capture-submit" style="padding: 6px 14px; font-size: 0.85rem;">
          Registrar
        </button>
      </div>

      <!-- Contextual Active Mode Widget -->
      ${renderActiveModeWidget(activeMode)}

      <!-- Summary Metrics Grid -->
      <div class="metrics-grid">
        <!-- Metric 1: Progresso -->
        <div class="metric-card">
          <div class="metric-label">Progresso do Dia</div>
          <div class="metric-value">${summary.progress_percent}%</div>
          <div class="progress-bar-bg">
            <div class="progress-bar-fill" style="width: ${summary.progress_percent}%;"></div>
          </div>
          <div class="metric-sub">${summary.completed_tasks} de ${summary.total_tasks} tarefas concluídas</div>
        </div>

        <!-- Metric 2: Tarefas Pendentes -->
        <div class="metric-card">
          <div class="metric-label">Pendências Hoje</div>
          <div class="metric-value" style="color: ${summary.pending_tasks > 0 ? 'var(--text-primary)' : 'var(--emerald-accent)'};">
            ${summary.pending_tasks}
          </div>
          <div class="metric-sub">${summary.overdue_count > 0 ? `⚠️ ${summary.overdue_count} tarefas atrasadas` : 'Sem atrasos acumulados'}</div>
        </div>

        <!-- Metric 3: Saldo de Tempo -->
        <div class="metric-card">
          <div class="metric-label">Tempo Planejado</div>
          <div class="metric-value">${summary.estimated_time_hours}h</div>
          <div class="metric-sub">${summary.available_time_hours}h restantes na capacidade diária (8h)</div>
        </div>

        <!-- Metric 4: Foco / Sobrecarga -->
        <div class="metric-card" style="${summary.is_overloaded ? 'border-color: rgba(244, 63, 94, 0.4);' : ''}">
          <div class="metric-label">Status da Carga</div>
          <div class="metric-value" style="font-size: 1.4rem; color: ${summary.is_overloaded ? 'var(--rose-accent)' : 'var(--emerald-accent)'};">
            ${summary.is_overloaded ? 'Sobrecarga' : 'Equilibrado'}
          </div>
          <div class="metric-sub">
            ${summary.overload_alert || 'Carga de trabalho compatível com descanso saudável'}
          </div>
        </div>
      </div>

      <!-- 2-Column Section Layout -->
      <div class="dashboard-columns">
        <!-- Main Left Column -->
        <div class="dashboard-left">
          <!-- Next Upcoming Appointment -->
          ${nextUpcomingCard(next_upcoming)}

          <!-- Urgent & High Priority Tasks -->
          <div class="card-section">
            <div class="section-title">
              <span>🔥 Prioridades de Hoje</span>
              <span style="font-size: 0.85rem; color: var(--text-muted); font-weight: 500;">
                ${urgent_tasks.length} prioritárias
              </span>
            </div>

            ${urgent_tasks.length === 0 ? `
              <div style="text-align: center; padding: 24px 0; color: var(--text-muted);">
                <span>✨ Nenhuma tarefa urgente pendente hoje. Bom trabalho!</span>
              </div>
            ` : `
              <div class="task-list">
                ${urgent_tasks.map((t) => renderTaskRow(t)).join('')}
              </div>
            `}
          </div>

          <!-- All Tasks of Today -->
          <div class="card-section">
            <div class="section-title">
              <span>📋 Todas as Tarefas do Dia</span>
              <button class="btn-secondary" id="btn-view-all-agenda" style="font-size: 0.8rem; padding: 4px 10px;">
                Ver Agenda Completa →
              </button>
            </div>

            ${todayTasks.length === 0 ? `
              <div style="text-align: center; padding: 30px 0; color: var(--text-muted);">
                <p>Nenhuma tarefa cadastrada para hoje.</p>
                <button class="btn-primary" id="btn-empty-add-task" style="margin-top: 12px;">
                  Adicionar Tarefa
                </button>
              </div>
            ` : `
              <div class="task-list">
                ${todayTasks.map((t) => renderTaskRow(t)).join('')}
              </div>
            `}
          </div>
        </div>

        <!-- Right Column: Timeline & Contextual Advice -->
        <div class="dashboard-right">
          <!-- Timeline Card -->
          <div class="card-section">
            <div class="section-title">
              <span>⏱️ Linha do Tempo</span>
              <span style="font-size: 0.8rem; color: var(--text-muted);">Hoje</span>
            </div>

            <div class="timeline-list">
              ${renderTimelineSlots(timeline)}
            </div>
          </div>

          <!-- Dica do Momento (Copiloto) -->
          <div class="card-section" style="background: linear-gradient(145deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.04) 100%); border-color: rgba(99, 102, 241, 0.25);">
            <div class="section-title" style="color: var(--primary);">
              <span>💡 Dica do Momento</span>
            </div>
            <p style="font-size: 0.9rem; line-height: 1.6; color: var(--text-secondary);">
              ${summary.is_overloaded 
                ? 'Seu dia tem mais de 8 horas planejadas. Experimente escolher as 3 tarefas indispensáveis e adiar as opcionais.'
                : summary.completed_tasks > 0 && summary.pending_tasks === 0
                ? 'Todas as tarefas de hoje foram concluídas! Aproveite o tempo para descanso, família ou autocuidado.'
                : 'Mantenha pausas regulares entre blocos de trabalho para sustentar a clareza mental e a disposição.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  `;
}

function nextUpcomingCard(next) {
  if (!next) {
    return `
      <div class="card-section" style="padding: 18px 24px; background: rgba(99, 102, 241, 0.04); border-color: rgba(99, 102, 241, 0.2);">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="font-size: 1.5rem;">🎉</span>
            <div>
              <div style="font-size: 0.8rem; font-weight: 700; text-transform: uppercase; color: var(--primary);">Próximo Compromisso</div>
              <div style="font-size: 0.95rem; font-weight: 600; color: var(--text-primary); margin-top: 2px;">
                Nenhum compromisso agendado para o restante do dia.
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  const pMeta = getPriorityMeta(next.priority);
  const locMeta = getLocationMeta(next.context_location);

  return `
    <div class="card-section" style="padding: 20px 24px; border-color: rgba(99, 102, 241, 0.35); background: rgba(99, 102, 241, 0.06);">
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
        <span style="font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--primary);">
          🎯 Próximo Foco Programado
        </span>
        ${next.due_time ? `
          <span style="font-family: var(--font-heading); font-weight: 700; font-size: 1.05rem; color: #ffffff;">
            ⏰ ${formatTimeBR(next.due_time)}
          </span>
        ` : ''}
      </div>

      <div style="font-size: 1.2rem; font-weight: 700; margin-bottom: 8px;">
        ${escapeHtml(next.title)}
      </div>

      <div style="display: flex; align-items: center; gap: 10px; font-size: 0.8rem;">
        <span class="badge ${pMeta.class}">${pMeta.icon} ${pMeta.label}</span>
        <span style="color: var(--text-muted);">${locMeta.icon} ${locMeta.label}</span>
        ${next.estimated_minutes > 0 ? `<span style="color: var(--text-muted);">⏱️ ${next.estimated_minutes} min</span>` : ''}
      </div>
    </div>
  `;
}

function renderTaskRow(task) {
  const isDone = task.status === 'completed';
  const pMeta = getPriorityMeta(task.priority);
  const locMeta = getLocationMeta(task.context_location);

  return `
    <div class="task-item ${isDone ? 'completed' : ''}" data-task-id="${task.id}">
      <div class="task-checkbox ${isDone ? 'checked' : ''}" data-action="toggle-task" data-id="${task.id}">
        ${isDone ? '✓' : ''}
      </div>

      <div class="task-info">
        <div class="task-title" data-action="edit-task" data-id="${task.id}">${escapeHtml(task.title)}</div>
        <div class="task-meta">
          <span class="badge ${pMeta.class}">${pMeta.label}</span>
          ${task.due_time ? `<span>⏰ ${formatTimeBR(task.due_time)}</span>` : ''}
          ${task.estimated_minutes > 0 ? `<span>⏱️ ${task.estimated_minutes} min</span>` : ''}
          <span>${locMeta.icon} ${locMeta.label}</span>
          ${task.category_name ? `<span style="color: ${task.category_color || 'var(--text-muted)'};">🏷️ ${escapeHtml(task.category_name)}</span>` : ''}
        </div>
      </div>

      <div class="task-actions" style="display: flex; gap: 4px;">
        <button class="btn-icon" data-action="edit-task" data-id="${task.id}" title="Editar" style="width: 32px; height: 32px; font-size: 0.85rem;">
          ✏️
        </button>
      </div>
    </div>
  `;
}

function renderTimelineSlots(timelineMap = {}) {
  const slots = [8, 9, 10, 11, 14, 15, 16, 17, 19, 20];

  return slots.map((hour) => {
    const hourKey = hour;
    const hourStr = `${String(hour).padStart(2, '0')}:00`;
    const items = timelineMap[hourKey] || [];

    return `
      <div class="timeline-slot">
        <div class="timeline-dot" style="${items.length > 0 ? 'background: var(--primary);' : ''}"></div>
        <div class="timeline-hour">${hourStr}</div>
        <div class="timeline-content" style="${items.length > 0 ? 'border-color: rgba(99, 102, 241, 0.3);' : 'opacity: 0.6;'}">
          ${items.length === 0 ? `
            <span style="font-size: 0.8rem; color: var(--text-muted);">Disponível</span>
          ` : `
            ${items.map((it) => `
              <div style="font-weight: 600; font-size: 0.88rem; color: var(--text-primary); margin-bottom: 2px;">
                ${escapeHtml(it.title)}
              </div>
              <div style="font-size: 0.75rem; color: var(--text-muted);">
                ${it.time} • ${it.category}
              </div>
            `).join('')}
          `}
        </div>
      </div>
    `;
  }).join('');
}

function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

export function attachDashboardEvents(container) {
  // Quick Capture Input
  const quickInput = container.querySelector('#quick-capture-input');
  const quickBtn = container.querySelector('#btn-quick-capture-submit');

  const handleQuickCapture = async () => {
    const title = quickInput.value.trim();
    if (!title) return;

    try {
      await api.createTask({
        title,
        priority: 'medium',
        due_date: new Date().toISOString().split('T')[0],
      });
      quickInput.value = '';
      showToast('Tarefa registrada com sucesso!', 'success');
      window.dispatchEvent(new CustomEvent('infinity:refresh-data'));
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  if (quickInput) {
    quickInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleQuickCapture();
    });
  }
  if (quickBtn) {
    quickBtn.addEventListener('click', handleQuickCapture);
  }

  // New Task button
  const newBtn = container.querySelector('#btn-quick-new-task') || container.querySelector('#btn-empty-add-task');
  if (newBtn) {
    newBtn.addEventListener('click', () => store.openTaskModal());
  }

  // Go to full agenda
  const agendaBtn = container.querySelector('#btn-view-all-agenda');
  if (agendaBtn) {
    agendaBtn.addEventListener('click', () => store.setView('agenda'));
  }

  // Task Toggle
  container.querySelectorAll('[data-action="toggle-task"]').forEach((el) => {
    el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.id;
      try {
        await api.toggleTask(id);
        window.dispatchEvent(new CustomEvent('infinity:refresh-data'));
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  });

  // Edit Task
  container.querySelectorAll('[data-action="edit-task"]').forEach((el) => {
    el.addEventListener('click', async () => {
      const id = el.dataset.id;
      try {
        const res = await api.getTask(id);
        if (res.success) {
          store.openTaskModal(res.data);
        }
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  });

  // Active Mode: Health Events
  const btnWater250 = container.querySelector('#btn-add-water-250');
  if (btnWater250) {
    btnWater250.addEventListener('click', () => {
      waterMl += 250;
      localStorage.setItem('infinity_water_today', String(waterMl));
      showToast(`💧 +250ml adicionados! Total: ${waterMl}ml hoje`, 'success');
      window.dispatchEvent(new CustomEvent('infinity:render'));
    });
  }

  const btnWater500 = container.querySelector('#btn-add-water-500');
  if (btnWater500) {
    btnWater500.addEventListener('click', () => {
      waterMl += 500;
      localStorage.setItem('infinity_water_today', String(waterMl));
      showToast(`🍶 +500ml adicionados! Total: ${waterMl}ml hoje`, 'success');
      window.dispatchEvent(new CustomEvent('infinity:render'));
    });
  }

  const btnWaterReset = container.querySelector('#btn-reset-water');
  if (btnWaterReset) {
    btnWaterReset.addEventListener('click', () => {
      waterMl = 0;
      localStorage.setItem('infinity_water_today', '0');
      showToast('Hidratação zerada para novo dia.', 'info');
      window.dispatchEvent(new CustomEvent('infinity:render'));
    });
  }

  // Active Mode: Pomodoro Events
  const btnPomoToggle = container.querySelector('#btn-pomodoro-toggle');
  if (btnPomoToggle) {
    btnPomoToggle.addEventListener('click', () => {
      if (pomodoroIsRunning) {
        clearInterval(pomodoroTimer);
        pomodoroIsRunning = false;
        btnPomoToggle.textContent = '▶️ Iniciar Foco';
        showToast('Cronômetro pausado.', 'info');
      } else {
        pomodoroIsRunning = true;
        btnPomoToggle.textContent = '⏸️ Pausar';
        showToast('🎯 Foco iniciado! Mantenha a atenção plena.', 'success');

        clearInterval(pomodoroTimer);
        pomodoroTimer = setInterval(() => {
          if (pomodoroSeconds > 0) {
            pomodoroSeconds--;
            const display = document.getElementById('pomodoro-timer-display');
            if (display) {
              const m = Math.floor(pomodoroSeconds / 60);
              const s = pomodoroSeconds % 60;
              display.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
            }
          } else {
            clearInterval(pomodoroTimer);
            pomodoroIsRunning = false;
            showToast('🎉 Bloco de foco finalizado! Faça uma pausa saudável de 5 minutos.', 'success', 8000);
            window.dispatchEvent(new CustomEvent('infinity:render'));
          }
        }, 1000);
      }
    });
  }

  const btnPomoReset = container.querySelector('#btn-pomodoro-reset');
  if (btnPomoReset) {
    btnPomoReset.addEventListener('click', () => {
      clearInterval(pomodoroTimer);
      pomodoroIsRunning = false;
      pomodoroSeconds = 25 * 60;
      window.dispatchEvent(new CustomEvent('infinity:render'));
      showToast('Cronômetro reiniciado (25min).', 'info');
    });
  }

  const btnPomoBreak = container.querySelector('#btn-pomodoro-break');
  if (btnPomoBreak) {
    btnPomoBreak.addEventListener('click', () => {
      clearInterval(pomodoroTimer);
      pomodoroIsRunning = false;
      pomodoroSeconds = 5 * 60;
      window.dispatchEvent(new CustomEvent('infinity:render'));
      showToast('☕ Pausa de 5 minutos preparada.', 'info');
    });
  }

  // Active Mode: Finance Event
  const btnFinance = container.querySelector('#btn-goto-business-finance');
  if (btnFinance) {
    btnFinance.addEventListener('click', () => {
      store.setView('business');
    });
  }

  // Active Mode: Study Event
  const btnStudy = container.querySelector('#btn-study-pomodoro');
  if (btnStudy) {
    btnStudy.addEventListener('click', () => {
      clearInterval(pomodoroTimer);
      pomodoroIsRunning = false;
      pomodoroSeconds = 45 * 60;
      store.setMode('focus');
      showToast('📚 Bloco de Estudos de 45 minutos preparado!', 'success');
    });
  }
}

// Module State for Contextual Widgets
let waterMl = parseInt(localStorage.getItem('infinity_water_today') || '750', 10);
let pomodoroSeconds = 25 * 60;
let pomodoroTimer = null;
let pomodoroIsRunning = false;

function renderActiveModeWidget(mode) {
  if (!mode || mode === 'my_day') return '';

  if (mode === 'health') {
    const targetMl = 2500;
    const pct = Math.min(100, Math.round((waterMl / targetMl) * 100));
    return `
      <div class="card-section" style="margin-bottom: 24px; background: linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, rgba(99, 102, 241, 0.05) 100%); border: 1px solid rgba(236, 72, 153, 0.25);">
        <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 12px;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <span style="font-size: 1.6rem;">❤️</span>
            <div>
              <div style="font-weight: 700; font-size: 1.05rem; color: var(--text-primary);">Painel de Saúde, Foco & Hidratação</div>
              <div style="font-size: 0.8rem; color: var(--text-muted);">Monitore água, pausas ergonômicas e equilíbrio biológico</div>
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <button class="btn-primary" id="btn-add-water-250" style="padding: 6px 12px; font-size: 0.82rem; background: #06b6d4; border-color: #06b6d4;">
              💧 +250ml Água
            </button>
            <button class="btn-secondary" id="btn-add-water-500" style="padding: 6px 12px; font-size: 0.82rem;">
              🍶 +500ml
            </button>
            <button class="btn-secondary" id="btn-reset-water" style="padding: 6px 10px; font-size: 0.82rem;" title="Zerar contador">
              🔄
            </button>
          </div>
        </div>

        <div style="margin-bottom: 12px;">
          <div style="display: flex; justify-content: space-between; font-size: 0.82rem; margin-bottom: 4px;">
            <span style="color: var(--text-secondary);">Hidratação Hoje: <strong>${waterMl}ml</strong> / ${targetMl}ml</span>
            <span style="font-weight: 700; color: #06b6d4;">${pct}%</span>
          </div>
          <div class="progress-bar-bg" style="height: 10px;">
            <div class="progress-bar-fill" style="width: ${pct}%; background: linear-gradient(90deg, #06b6d4, #3b82f6);"></div>
          </div>
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; font-size: 0.82rem; color: var(--text-secondary);">
          <div style="padding: 10px 14px; background: rgba(255, 255, 255, 0.03); border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
            <strong>👁️ Descanso Visual (20-20-20):</strong> A cada 20 min de tela, olhe a 6 metros de distância por 20 seg.
          </div>
          <div style="padding: 10px 14px; background: rgba(255, 255, 255, 0.03); border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
            <strong>🧘 Postura:</strong> Apoie os dois pés no chão, ombros para trás e cotovelos a 90°.
          </div>
        </div>
      </div>
    `;
  }

  if (mode === 'focus') {
    const mins = Math.floor(pomodoroSeconds / 60);
    const secs = pomodoroSeconds % 60;
    const formatted = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    return `
      <div class="card-section" style="margin-bottom: 24px; background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.06) 100%); border: 1px solid rgba(99, 102, 241, 0.3);">
        <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 14px;">
          <div style="display: flex; align-items: center; gap: 14px;">
            <span style="font-size: 2rem;">🎯</span>
            <div>
              <div style="font-weight: 800; font-size: 1.1rem; color: var(--text-primary);">Sessão de Hiperfoco (Técnica Pomodoro)</div>
              <div style="font-size: 0.82rem; color: var(--text-muted);">Elimine distrações, silencie o celular e foque em uma única tarefa</div>
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: 12px;">
            <div id="pomodoro-timer-display" style="font-size: 2rem; font-weight: 900; font-family: monospace; color: var(--primary); letter-spacing: 2px;">
              ${formatted}
            </div>
            <button class="btn-primary" id="btn-pomodoro-toggle" style="padding: 8px 18px; font-weight: 700;">
              ${pomodoroIsRunning ? '⏸️ Pausar' : '▶️ Iniciar Foco'}
            </button>
            <button class="btn-secondary" id="btn-pomodoro-reset" style="padding: 8px 12px;" title="Reiniciar para 25min">
              🔄 25m
            </button>
            <button class="btn-secondary" id="btn-pomodoro-break" style="padding: 8px 12px;" title="Pausa Curta 5min">
              ☕ 5m
            </button>
          </div>
        </div>
      </div>
    `;
  }

  if (mode === 'finance') {
    return `
      <div class="card-section" style="margin-bottom: 24px; background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(99, 102, 241, 0.05) 100%); border: 1px solid rgba(16, 185, 129, 0.3);">
        <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;">
          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="font-size: 1.8rem;">💰</span>
            <div>
              <div style="font-weight: 800; font-size: 1.05rem; color: var(--text-primary);">Modo Finanças & Fluxo de Caixa</div>
              <div style="font-size: 0.8rem; color: var(--text-muted);">Visão rápida de recebimentos, orçamentos e pendências financeiras</div>
            </div>
          </div>
          <div style="display: flex; gap: 8px;">
            <button class="btn-primary" id="btn-goto-business-finance" style="padding: 6px 14px; font-size: 0.85rem;">
              💼 Abrir Módulo Meu Negócio
            </button>
          </div>
        </div>
      </div>
    `;
  }

  if (mode === 'study') {
    return `
      <div class="card-section" style="margin-bottom: 24px; background: linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(99, 102, 241, 0.05) 100%); border: 1px solid rgba(245, 158, 11, 0.3);">
        <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;">
          <div style="display: flex; align-items: center; gap: 12px;">
            <span style="font-size: 1.8rem;">📚</span>
            <div>
              <div style="font-weight: 800; font-size: 1.05rem; color: var(--text-primary);">Modo Estudos & Desenvolvimento Contínuo</div>
              <div style="font-size: 0.8rem; color: var(--text-muted);">Organize livros, artigos técnicos, cursos e notas de estudo</div>
            </div>
          </div>
          <div>
            <button class="btn-primary" id="btn-study-pomodoro" style="padding: 6px 14px; font-size: 0.85rem;">
              🎯 Bloco de Estudo (45 min)
            </button>
          </div>
        </div>
      </div>
    `;
  }

  return '';
}
